import type { Location } from 'slate';
import { Editor, Text, Transforms } from 'slate';

import {
  isEditWithSource,
  isParagraphElement,
  isTableCellElement,
  isTableRowElement,
  setGlyphSuggestedEdits,
} from '../../internal';

/**
 * Stores the suggestion's reasoning on the first sourced edit (in document
 * order) inside `at` that carries the suggestion's groupId. Block suggestions
 * have no suggestion-marker pair — the sourced edits themselves are the
 * suggestion — so this is where the user-facing explanation lives. Readers
 * take the first reasoning they find among a group's edits, so multi-run
 * suggestions calling this once per run are fine (any surviving run still
 * explains itself).
 */
export function stampReasoningOnFirstSourcedEdit(
  editor: Editor,
  options: { at: Location; groupId: string; reasoning: string }
): void {
  const { at, groupId, reasoning } = options;

  for (const [node, path] of Editor.nodes(editor, { at, voids: true })) {
    if (Text.isText(node) || isTableRowElement(node) || isTableCellElement(node)) {
      const edits = node.suggestedEdits ?? [];
      const index = edits.findIndex(
        edit => isEditWithSource(edit) && edit.source.groupId === groupId
      );
      if (index === -1) continue;

      const nextEdits = edits.map((edit, editIndex) =>
        editIndex === index ? { ...edit, reasoning } : edit
      );
      Transforms.setNodes(editor, { suggestedEdits: nextEdits }, { at: path, voids: true });
      return;
    }

    if (!isParagraphElement(node)) continue;
    const glyphEdits = node.glyphProps?.suggestedEdits ?? [];
    const glyphIndex = glyphEdits.findIndex(
      edit => isEditWithSource(edit) && edit.source.groupId === groupId
    );
    if (glyphIndex === -1) continue;

    setGlyphSuggestedEdits(
      editor,
      path,
      glyphEdits.map((edit, editIndex) =>
        editIndex === glyphIndex ? { ...edit, reasoning } : edit
      )
    );
    return;
  }

  /**
   * Every apply transform stamps at least one sourced edit before calling
   * this, so finding none means the suggestion applied as a no-op — surface
   * it rather than shipping a suggestion nothing can display or resolve.
   */
  throw new Error(`No sourced edit carrying groupId '${groupId}' found to store reasoning on`);
}
